import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  alpha
} from '@mui/material';
import { Icon } from '@iconify/react';
import { styled, useTheme } from '@mui/material/styles';
import Page from '../components/Page';
import { ocpiModules } from 'src/components/ocpi/constants/ocpi.constant';

// Import icons
// import dashboardFill from '@iconify/icons-eva/bar-chart-fill';
// import pinFill from '@iconify/icons-eva/pin-fill';
// import creditCardFill from '@iconify/icons-eva/credit-card-fill';
// import peopleFill from '@iconify/icons-eva/people-fill';
import syncFill from '@iconify/icons-eva/sync-fill';
import arrowForwardFill from '@iconify/icons-eva/arrow-forward-fill';
// import pieChartFill from '@iconify/icons-eva/pie-chart-2-fill';

const StyledCard = styled(Card)(({ theme }) => ({
  minHeight: 180,
  height: '100%',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(15,23,42,0.95) 0%, rgba(2,6,23,0.92) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,0.92) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 22px 60px rgba(0,0,0,0.6)'
      : '0 2px 8px rgba(0,0,0,0.04)',
  padding: 0,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 28px 70px ${alpha(theme.palette.common.black, 0.65)}`
        : `0 8px 24px ${alpha(theme.palette.primary.main, 0.10)}`,
    borderColor: alpha(theme.palette.primary.main, 0.6),
    '& .card-icon': {
      transform: 'scale(1.08)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    '& .arrow-icon': {
      transform: 'translateX(2px)',
    },
  },
}));

const IconWrapper = styled(Avatar)(({ theme, bgcolor }) => ({
  width: 48,
  height: 48,
  background: bgcolor || theme.palette.primary.main,
  marginBottom: theme.spacing(1.5),
  boxShadow:
    theme.palette.mode === 'dark'
      ? `0 10px 24px ${alpha(bgcolor || theme.palette.primary.main, 0.45)}`
      : `0 2px 8px ${alpha(bgcolor || theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s ease',
  '& .MuiSvgIcon-root': {
    fontSize: 28,
  },
}));

const OCPIManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  

  const handleModuleClick = (module) => {
    if (module.status === 'Active') {
      navigate(module.path, { state: { fromOCPIManagement: true } });
    }
  };

  return (
    <Page title="OCPI Management">
      <Box
        sx={{
          backgroundImage:
            theme.palette.mode === 'dark'
              ? 'radial-gradient(circle at 20% 20%, rgba(9,12,22,0.95) 0%, rgba(2,6,23,0.98) 55%)'
              : 'linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,1) 70%)',
          py: { xs: 2, md: 3 }
        }}
      >
        <Container maxWidth="xl">
        <Box>
          {/* Boxed Header Section */}
          <Card
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 26px 65px rgba(0,0,0,0.65)'
                  : theme.shadows[3],
              backgroundImage:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(14,21,37,0.95) 0%, rgba(17,26,45,0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(247,249,252,0.92) 100%)'
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background:
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                    : 'linear-gradient(135deg, #00d25b 0%, #00b84a 100%)',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? `0 12px 28px ${alpha(theme.palette.common.black, 0.65)}`
                    : `0 4px 12px ${alpha('#00d25b', 0.18)}`,
              }}
            >
              <Icon icon={syncFill} width={28} height={28} style={{ color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.2 }}>
                OCPI Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.2 }}>
                Open Charge Point Interface - Version 2.1 & 2.2
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
                Centralized hub for managing all OCPI operations, partner integrations, and data sync
              </Typography>
            </Box>
          </Card>

          {/* Cards Section (full width, below header) */}
          <Grid container spacing={2}>
            {ocpiModules.map((module, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StyledCard onClick={() => handleModuleClick(module)}>
                  <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {/* Icon and Title in a row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <IconWrapper className="card-icon" bgcolor={module.color} sx={{ minWidth: 40, minHeight: 40, width: 40, height: 40 }}>
                        <Icon icon={module.icon} style={{ color: 'white', fontSize: 22 }} />
                      </IconWrapper>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
                        {module.title === 'CDRs' ? 'CDRs (Charge Detail Records)' : module.title}
                      </Typography>
                    </Box>
                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.97rem', flexGrow: 1 }}>
                      {module.description}
                    </Typography>
                    {/* Open Button (white, outlined, full width) */}
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        mt: 1,
                        borderRadius: 1,
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.04)
                            : '#fff',
                        borderColor: theme.palette.divider,
                        color: 'text.primary',
                        fontWeight: 600,
                        fontSize: '1rem',
                        minHeight: 36,
                        textTransform: 'none',
                        boxShadow: 'none',
                        justifyContent: 'space-between',
                        px: 2,
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha(module.color, 0.15)
                              : '#f4f6f8',
                          borderColor: module.color,
                        },
                      }}
                      onClick={() => handleModuleClick(module)}
                      endIcon={
                        <Icon 
                          icon={arrowForwardFill} 
                          className="arrow-icon"
                          style={{ transition: 'transform 0.3s ease', fontSize: '18px' }} 
                        />
                      }
                      disabled={module.status !== 'Active'}
                    >
                      Open
                    </Button>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

        {/* OCPI Status and Quick Actions at the bottom */}
        {/* <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                OCPI Status
              </Typography>
              <Box>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Connected Partners</Typography>
                    <Typography sx={{ color: '#2e7d32', fontWeight: 700 }}>3</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Active Locations</Typography>
                    <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>12</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Active Tariffs</Typography>
                    <Typography sx={{ color: '#ed6c02', fontWeight: 700 }}>4</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>CDRs Today</Typography>
                    <Typography sx={{ color: '#388e3c', fontWeight: 700 }}>45</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Last Sync</Typography>
                    <Typography sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 13 }}>2 minutes ago</Typography>
                  </Box>
                </Stack>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Quick Actions
              </Typography>
              <Stack spacing={1.5}>
                <Button variant="outlined" fullWidth startIcon={<Icon icon={dashboardFill} />} sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/ocpi-dashboard')}>
                  Open Dashboard
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Icon icon={pieChartFill} />} sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/ocpi/analytics')}>
                  View Analytics
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Icon icon={creditCardFill} />} sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/tariffs')}>
                  Manage Tariffs
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Icon icon={syncFill} />} sx={{ justifyContent: 'flex-start' }} onClick={() => { alert('Force Sync All Data triggered!')}}>
                  Force Sync All Data
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Icon icon={peopleFill} />} sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/partners')}>
                  Add New Partner
                </Button>
                <Button variant="outlined" fullWidth startIcon={<Icon icon={pinFill} />} sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/ocpi-locations')}>
                  Add New Location
                </Button>
              </Stack>
            </Card>
          </Grid>
        </Grid> */}
        </Box>
      </Container>
      </Box>
    </Page>
  );
};

export default OCPIManagement;
